import { isSameDay } from 'date-fns';
import { RefObject, useCallback, useRef } from 'react';

import { DatePillDropdownAction } from '~/modules/date-pill-dropdown/DatePillDropdown';
import { formatPillDate } from '~/views/news/utils/utils';

interface UseListScrollProps<T> {
  listRef: RefObject<HTMLDivElement | null>;
  items: T[];
  getItemDate: (item: T) => Date;
  handleSelectSpecificDate: (fromTime: string) => void;
}

export const useListScroll = <T>({
  listRef,
  items,
  getItemDate,
  handleSelectSpecificDate,
}: UseListScrollProps<T>) => {
  const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const registerGroupRef = useCallback(
    (key: string) => (element: HTMLDivElement | null) => {
      if (element) {
        groupRefs.current.set(key, element);
      } else {
        groupRefs.current.delete(key);
      }
    },
    []
  );

  const scrollToPosition = (top = 0, behavior: ScrollBehavior = 'smooth') => {
    const element = listRef.current;

    if (!element) {
      return;
    }

    element.scrollTo({
      top: top - element.offsetTop - 10,
      behavior,
    });
  };

  const handleDatePillChange = (action: DatePillDropdownAction) => {
    const { date } = action;
    const actionDate = new Date(date);
    const formattedPillDate = formatPillDate(actionDate);
    const hasBeenFetched = items.find(item =>
      isSameDay(actionDate, getItemDate(item))
    );

    const pillDateDom = groupRefs.current.get(formattedPillDate);

    if (!hasBeenFetched) {
      handleSelectSpecificDate(date);
    } else if (hasBeenFetched && pillDateDom) {
      scrollToPosition(pillDateDom.offsetTop);
    }
  };

  return { scrollToPosition, handleDatePillChange, registerGroupRef };
};
